import type { Localized } from './home.content';

/**
 * Client testimonials, supplied by the clients themselves (September 2026).
 *
 * Wording is kept as written. The only edits are house-style punctuation
 * (em dashes restructured as a colon and parentheses) and one typo fix
 * ("byr favorite" → "our favorite"). Arabic versions are translations and
 * are labelled as such on the page.
 *
 * Do NOT emit Review or AggregateRating JSON-LD from these: first-party
 * testimonials are not eligible for review rich results.
 */

export type TestimonialId =
  | 'frame-studio'
  | 'refine-studio'
  | 'the-pilates-room'
  | 'flex-flow'
  | 'gaby-reca';

export interface Testimonial {
  id: TestimonialId;
  company: string;
  logo: string;
  person?: Localized;
  role?: Localized;
  /** Short pull-quote for tabs and cards. Must be a verbatim sentence span. */
  excerpt: Localized;
  paragraphs: { en: string[]; ar: string[] };
}

export const TESTIMONIALS: Record<TestimonialId, Testimonial> = {
  'frame-studio': {
    id: 'frame-studio',
    company: 'Frame Studio',
    logo: 'frame-studio.png',
    excerpt: {
      en: 'Booking a class takes seconds, the flow is clean and intuitive, and our members genuinely enjoy using it.',
      ar: 'حجز الحصة يستغرق ثوانٍ، والتجربة واضحة وسهلة، وأعضاؤنا يستمتعون باستخدامه فعلًا.',
    },
    paragraphs: {
      en: [
        'Working with ASK AI has been one of the best decisions we made in building Frame. From day one they understood that a boutique studio lives or dies by the details, and they built us an app that reflects that: booking a class takes seconds, the flow is clean and intuitive, and our members genuinely enjoy using it. We’ve had clients comment on how effortless it is, which is rare praise for a booking system.',
        'What impressed us most is that nothing was ever “not possible.” Every time we came to them with an idea (a custom feature, a change to how something worked, a request specific to how we run things), they found a way to make it happen, and usually made it better than we imagined.',
        'The back end has been just as valuable. The CRM gives us real visibility into our members and our operations, it’s genuinely easy for the team to use, and it’s helped us make smarter decisions about everything from scheduling to retention. They’re not just developers, they’re partners in how we run the studio. We couldn’t recommend them more highly.',
      ],
      ar: [
        'كان العمل مع ASK AI من أفضل القرارات التي اتخذناها في بناء Frame. منذ اليوم الأول أدركوا أن الاستوديو المتخصص يقوم على التفاصيل، فبنوا لنا تطبيقًا يعكس ذلك: حجز الحصة يستغرق ثوانٍ، والتجربة واضحة وسهلة، وأعضاؤنا يستمتعون باستخدامه فعلًا. وقد أشاد عملاؤنا بسهولته، وهذا ثناء نادر لنظام حجز.',
        'أكثر ما أدهشنا أنه لم يكن هناك شيء «مستحيل». في كل مرة جئناهم بفكرة (ميزة مخصصة، أو تعديل على طريقة عمل شيء ما، أو طلب خاص بطريقة إدارتنا)، وجدوا طريقة لتنفيذها، وغالبًا جاءت أفضل مما تخيلنا.',
        'والنظام الإداري لا يقل قيمة. يمنحنا نظام إدارة العملاء رؤية حقيقية لأعضائنا وعملياتنا، وهو سهل الاستخدام لفريقنا، وساعدنا على اتخاذ قرارات أذكى في كل شيء من الجدولة إلى الاحتفاظ بالأعضاء. هم ليسوا مطورين فحسب، بل شركاء في إدارة الاستوديو، ونوصي بهم بكل ثقة.',
      ],
    },
  },
  'refine-studio': {
    id: 'refine-studio',
    company: 'Refine Studio',
    logo: 'refine-studio.png',
    excerpt: {
      en: 'The AI integration has definitely been a standout for us.',
      ar: 'كان تكامل الذكاء الاصطناعي من أبرز ما لفت انتباهنا.',
    },
    paragraphs: {
      en: [
        'We’ve been really impressed so far. The platform is smooth, intuitive, and just makes sense, both from a client perspective and behind the scenes for our team.',
        'The built-in support and guidance make a real difference, and the AI integration has definitely been a standout for us. It feels like a platform that’s really keeping up with where things are going.',
      ],
      ar: [
        'أعجبتنا المنصة كثيرًا حتى الآن. فهي سلسة وسهلة ومنطقية، سواء من جهة العملاء أو خلف الكواليس لفريقنا.',
        'الدعم والإرشاد المدمجان في المنصة يصنعان فرقًا حقيقيًا، وكان تكامل الذكاء الاصطناعي من أبرز ما لفت انتباهنا. نشعر أنها منصة تواكب فعلًا ما هو قادم.',
      ],
    },
  },
  'the-pilates-room': {
    id: 'the-pilates-room',
    company: 'The Pilates Room',
    logo: 'the-pilates-room.png',
    excerpt: {
      en: 'Ask Flow has been our favorite booking system! It\u2019s fast, seamless, and incredibly easy to use.',
      ar: 'كان Ask Flow نظام الحجز المفضل لدينا! سريع وسلس وسهل الاستخدام إلى حد كبير.',
    },
    paragraphs: {
      en: [
        'Ask Flow has been our favorite booking system! It’s fast, seamless, and incredibly easy to use. What truly sets it apart is your amazing customer service; your team is always responsive, supportive, and quick to handle any request we have. Really appreciate all the efforts you and your team are putting into this. يعطيكم العافية',
      ],
      ar: [
        'كان Ask Flow نظام الحجز المفضل لدينا! سريع وسلس وسهل الاستخدام إلى حد كبير. وما يميزه حقًا خدمة العملاء الرائعة، ففريقكم دائمًا متجاوب وداعم وسريع في تلبية أي طلب لدينا. نقدّر كل الجهود التي تبذلونها أنتم وفريقكم. يعطيكم العافية',
      ],
    },
  },
  'flex-flow': {
    id: 'flex-flow',
    company: 'Flex&Flow',
    logo: 'flexflow.png',
    excerpt: {
      en: 'If we need a feature that isn’t currently available, they are often able to develop and implement it very quickly.',
      ar: 'إذا احتجنا ميزة غير متوفرة حاليًا، فغالبًا ما يطوّرونها ويطلقونها بسرعة كبيرة.',
    },
    paragraphs: {
      en: [
        'What I value most about the Ask AI platform is how user-friendly and intuitive it is, making it easy to navigate for everyone, regardless of their technical background or experience.',
        'The Ask AI team is also always available, quick to respond, and genuinely helpful when it comes to solving any issues. What we appreciate most, however, is their openness to feedback. If we need a feature that isn’t currently available, they are often able to develop and implement it very quickly. This makes us feel truly heard as a business and allows us to continuously improve both our own experience and, most importantly, the experience we offer our customers.',
      ],
      ar: [
        'أكثر ما أقدّره في منصة Ask AI سهولة استخدامها ووضوحها، فيستطيع الجميع التنقل فيها بسهولة مهما كانت خلفيتهم التقنية أو خبرتهم.',
        'وفريق Ask AI متاح دائمًا وسريع الاستجابة ويساعد فعلًا في حل أي مشكلة. لكن أكثر ما نقدّره هو انفتاحهم على الملاحظات. إذا احتجنا ميزة غير متوفرة حاليًا، فغالبًا ما يطوّرونها ويطلقونها بسرعة كبيرة. وهذا يشعرنا بأن صوتنا مسموع كمنشأة، ويتيح لنا تحسين تجربتنا باستمرار، والأهم من ذلك تحسين التجربة التي نقدمها لعملائنا.',
      ],
    },
  },
  'gaby-reca': {
    id: 'gaby-reca',
    company: 'Gaby Reca Academy',
    logo: 'gaby-reca-academy.png',
    excerpt: {
      en: 'They’re always open to feedback and willing to work with us to find solutions that fit our business needs.',
      ar: 'هم منفتحون دائمًا على الملاحظات ومستعدون للعمل معنا لإيجاد حلول تناسب احتياجات أعمالنا.',
    },
    paragraphs: {
      en: [
        'What I appreciate most about the software is how user-friendly and flexible the platform is. The team is very responsive and supportive, and they’re always open to feedback and willing to work with us to find solutions that fit our business needs.',
      ],
      ar: [
        'أكثر ما أقدّره في هذا النظام سهولة استخدام المنصة ومرونتها. الفريق متجاوب وداعم جدًا، وهم منفتحون دائمًا على الملاحظات ومستعدون للعمل معنا لإيجاد حلول تناسب احتياجات أعمالنا.',
      ],
    },
  },
};

export const TESTIMONIALS_SECTION = {
  label: { en: 'In their words', ar: 'بكلماتهم' },
  heading: {
    en: 'What some of our clients say',
    ar: 'ماذا يقول بعض عملائنا',
  },
  translatedNote: {
    en: '',
    ar: 'ترجمة من الإنجليزية',
  },
  readFull: { en: 'Read the full review', ar: 'اقرأ المراجعة كاملة' },
  showLess: { en: 'Show less', ar: 'عرض أقل' },
} as const;

/** Order for the landing-page wall: the long Frame story leads. */
export const TESTIMONIAL_WALL: TestimonialId[] = [
  'frame-studio',
  'flex-flow',
  'the-pilates-room',
  'refine-studio',
  'gaby-reca',
];
